// Mail V2 RecipientsChips (2026-07-24): parser / validator / serializer for chip input.
//
// 纯函数,不依赖 React。被 RecipientsChips 组件使用。
//
// 设计:
//   - 单一存储形态 = draftHook.fields.to/cc (string, comma-separated)
//   - UI 边界完成 chips ↔ string 转换(本文件 + RecipientsChips 协作)
//   - 支持分隔符:`,` `;` `\n` `\t`
//   - 支持 `"Name <email@example.com>"` 形式 (displayName 可含逗号)
//   - invalid 输入以 raw chip 形式保留,跨 onChange round-trip 不丢失
//
// 参考:
//   - 现有 MailV2Page.normEmail (L794) 与 filterOutSelf (L809) 的 email 比较逻辑
//   - SMTP backend 接受逗号分隔串并 split 成多个 recipient

export type RecipientChip = {
  /** React key (stable: lowercased email for valid; `invalid-${idx}` for invalid) */
  key: string;
  /** lowercased email part (always) */
  email: string;
  /** "Doe, John" from `"Doe, John" <john@x.com>`; null if absent */
  displayName: string | null;
  /** 标记为 invalid 输入(保留 raw,不参与 SMTP 发送) */
  invalid?: boolean;
  invalidReason?: string;
  /** 原始输入(仅 invalid chip 保留,跨 round-trip) */
  raw?: string;
};

const SEPARATOR_RE = /[,;\n\t]/;
// `Name <email>` 解析;非贪婪匹配 displayName,允许 displayName 含逗号、空白、特殊字符。
const NAME_EMAIL_RE = /^\s*(.*?)\s*<([^>]+)>\s*$/;
// RFC 缓标准的邮箱校验:local@domain.tld,不允许空白。
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email);
}

/**
 * 解析单个 token:
 *   - "Name <email@x.com>" → {email, displayName: "Name"}
 *   - "Doe, John <john@x.com>" → {email, displayName: "Doe, John"} (displayName 含逗号)
 *   - "email@x.com" → {email, displayName: null}
 *   - "foo bar" / "invalid" → {invalid: true, raw, invalidReason}
 */
export function parseOneToken(token: string): RecipientChip {
  const trimmed = token.trim();
  if (!trimmed) {
    return {
      key: `invalid-${Math.random().toString(36).slice(2, 9)}`,
      email: "",
      displayName: null,
      invalid: true,
      invalidReason: "empty",
      raw: token,
    };
  }

  const named = trimmed.match(NAME_EMAIL_RE);
  if (named && named[1] !== undefined && named[2] !== undefined) {
    const displayName = named[1].trim() || null;
    const emailPart = named[2].trim();
    const normalized = normalizeEmail(emailPart);
    if (isValidEmail(normalized)) {
      return {
        key: normalized,
        email: normalized,
        displayName,
      };
    }
    return {
      key: `invalid-${Math.random().toString(36).slice(2, 9)}`,
      email: normalized,
      displayName,
      invalid: true,
      invalidReason: "invalid_email",
      raw: trimmed,
    };
  }

  // 无 `<>` 包裹:尝试作为纯 email
  const normalized = normalizeEmail(trimmed);
  if (isValidEmail(normalized)) {
    return {
      key: normalized,
      email: normalized,
      displayName: null,
    };
  }
  return {
    key: `invalid-${Math.random().toString(36).slice(2, 9)}`,
    email: normalized,
    displayName: null,
    invalid: true,
    invalidReason: "invalid_email",
    raw: trimmed,
  };
}

/**
 * 按 `,` `;` `\n` `\t` split 文本,逐 token 解析。
 * 空 token 自动 drop(避免末尾分隔符生成无效 chip)。
 */
export function parseRecipientText(text: string): RecipientChip[] {
  if (!text) return [];
  const tokens: string[] = [];
  let start = 0;
  let angleDepth = 0;
  let quote: string | null = null;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === "<") {
      angleDepth += 1;
      continue;
    }
    if (ch === ">") {
      angleDepth = Math.max(0, angleDepth - 1);
      continue;
    }
    if (angleDepth === 0 && SEPARATOR_RE.test(ch)) {
      tokens.push(text.slice(start, i));
      start = i + 1;
    }
  }
  tokens.push(text.slice(start));
  const chips: RecipientChip[] = [];
  for (const t of tokens) {
    if (!t.trim()) continue;
    chips.push(parseOneToken(t));
  }
  return chips;
}

/**
 * dedupe:
 *   - invalid chip 全部保留(顺序不变)
 *   - valid chip 按 lowercased email 去重,首次出现胜出
 */
export function dedupeChips(chips: RecipientChip[]): RecipientChip[] {
  const seen = new Set<string>();
  const out: RecipientChip[] = [];
  for (const c of chips) {
    if (c.invalid) {
      out.push(c);
      continue;
    }
    if (seen.has(c.email)) continue;
    seen.add(c.email);
    out.push(c);
  }
  return out;
}

/**
 * UI 渲染入口:把 comma-separated string → chip list。
 * 用于每 render 显示。
 */
export function recipientsToChips(value: string): RecipientChip[] {
  if (!value || !value.trim()) return [];
  return dedupeChips(parseRecipientText(value));
}

/**
 * setField 入口:把 chip list → comma-separated string。
 *   - valid chip → `displayName ? "${displayName} <${email}>" : email`
 *   - invalid chip → raw (跨 round-trip 还原)
 *   - 用 `, ` (comma+space) 作分隔符,与现有 handleReplyAll.joinList 一致
 */
export function serializedChipsToString(chips: RecipientChip[]): string {
  if (!chips || chips.length === 0) return "";
  const parts: string[] = [];
  for (const c of chips) {
    if (c.invalid) {
      if (c.raw) parts.push(c.raw);
      continue;
    }
    parts.push(c.displayName ? `${c.displayName} <${c.email}>` : c.email);
  }
  return parts.join(", ");
}
